import { client } from './client';

import { Gender, UpdateUserPreferences, User, UserPreferences } from '../../models/User';
import { throwIfSupabaseError } from './throwIfSupabaseError';

const table = 'user_preferences';

function getDefaultUserPreferences(userId: User['id']): UserPreferences {
    return {
        userId,
        notifyBeforeGameOpen: false,
        notifySpotsStillOpen: false,
        notifyBeforeGameStart: false,
        notifyGameApplications: false,
        acceptedGmTc: false,
        analyticsConsent: null,
        preferredGender: Gender.Male,
        preferVerticalLayout: false,
    };
}

// The DB CHECK constraint only allows 'male' | 'female', but the column is
// typed as plain text. Coerce any unexpected value back to Male so the UI
// never wedges on a stale row.
function coerceGender(value: unknown): Gender {
    return value === Gender.Female ? Gender.Female : Gender.Male;
}

export async function getUserPreferences(userId: User['id']): Promise<UserPreferences> {
    const { data, error } = await client
        .from(table)
        .select()
        .eq('user_id', userId)
        .single();

    // Silent fallback when the row is missing or unreadable — the UI still
    // needs a sane preferences object to render.
    if (error || !data) {
        return getDefaultUserPreferences(userId);
    }

    return {
        userId: data.user_id,
        notifyBeforeGameOpen: data.notify_before_game_open,
        notifySpotsStillOpen: data.notify_spots_still_open,
        notifyBeforeGameStart: data.notify_before_game_start,
        notifyGameApplications: data.notify_game_applications,
        acceptedGmTc: data.accepted_gm_tc,
        analyticsConsent: data.analytics_consent ?? null,
        preferredGender: coerceGender(data.preferred_gender),
        preferVerticalLayout: data.prefer_vertical_layout === true,
    }
}

export async function setUserPreferences(dto: UpdateUserPreferences): Promise<void> {
    const response = await client
        .from(table)
        .upsert({
            user_id: dto.userId,
            notify_before_game_open: dto.notifyBeforeGameOpen,
            notify_spots_still_open: dto.notifySpotsStillOpen,
            notify_before_game_start: dto.notifyBeforeGameStart,
            notify_game_applications: dto.notifyGameApplications,
            preferred_gender: dto.preferredGender,
            prefer_vertical_layout: dto.preferVerticalLayout,
        });
    throwIfSupabaseError(response);
}

// Public read of another user's preferred gender via the dedicated view
// (`user_preferred_genders`). Returns `Gender.Male` when no row exists yet
// — matches the column default and the project-wide fallback.
//
// Unlike `getUserPreferences`, this is callable for *any* user id; the view
// is granted to anon + authenticated and exposes only the gender column.
export async function getPreferredGender(userId: User['id']): Promise<Gender> {
    const { data, error } = await client
        .from('user_preferred_genders')
        .select('preferred_gender')
        .eq('user_id', userId)
        .maybeSingle();

    if (error || !data) {
        return Gender.Male;
    }

    return coerceGender(data.preferred_gender);
}

// Consent has its own writer so the consent modal and Settings toggle can
// touch the column without having to round-trip the rest of the preference
// payload. Authed-only: the row is keyed by `user_id` and RLS forbids cross-
// account writes.
export async function setAnalyticsConsent(userId: User['id'], consent: boolean | null): Promise<void> {
    const response = await client
        .from(table)
        .upsert({
            user_id: userId,
            analytics_consent: consent,
        });
    throwIfSupabaseError(response);
}
